import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../reduxStore/Store";
import { FamilleData } from "../../models/models";
import { GeneriqueDataType } from "../../constants/enums";
import { fetchAllFamillesTree } from "../../reduxStore/FamilleSlice";

interface FamilleTreeViewProps {
  type: GeneriqueDataType;
  /** Clic sur un nœud sélectionnable. */
  onSelect: (famille: FamilleData) => void;
  /** Famille surlignée comme sélection courante. */
  selectedId?: number | null;
  /** Famille non sélectionnable, ainsi que ses descendants (évite les cycles lors du choix d'un parent). */
  excludeId?: number;
  /** Changer cette valeur force le rechargement (ex : après modification de la hiérarchie). */
  reloadKey?: unknown;
  maxHeight?: number | string;
}

const normalize = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const FamilleTreeView: React.FC<FamilleTreeViewProps> = ({
  type,
  onSelect,
  selectedId,
  excludeId,
  reloadKey,
  maxHeight = 420,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allFamilles, allFamillesType, allFamillesLoading } = useSelector(
    (state: any) => state.FamilleSlice
  );
  const familles: FamilleData[] = allFamillesType === type ? allFamilles : [];
  const loading = allFamillesLoading || allFamillesType !== type;

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    dispatch(fetchAllFamillesTree({ type }));
  }, [dispatch, type, reloadKey]);

  // Index parent → enfants ; racines = sans parent ou parent absent du jeu de données
  const { roots, childrenOf, byId } = useMemo(() => {
    const byId = new Map<number, FamilleData>();
    familles.forEach((f) => byId.set(f.id, f));
    const childrenOf = new Map<number, FamilleData[]>();
    const roots: FamilleData[] = [];
    familles.forEach((f) => {
      const pid = f.parent_id ?? null;
      if (pid !== null && byId.has(pid) && pid !== f.id) {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid)!.push(f);
      } else {
        roots.push(f);
      }
    });
    return { roots, childrenOf, byId };
  }, [familles]);

  // excludeId + tous ses descendants
  const excluded = useMemo(() => {
    const set = new Set<number>();
    if (excludeId == null) return set;
    const stack = [excludeId];
    while (stack.length) {
      const id = stack.pop()!;
      if (set.has(id)) continue;
      set.add(id);
      (childrenOf.get(id) ?? []).forEach((c) => stack.push(c.id));
    }
    return set;
  }, [excludeId, childrenOf]);

  // Recherche : correspondances + leurs ancêtres (pour garder le chemin visible)
  const term = normalize(search.trim());
  const { visibleIds, matchIds } = useMemo(() => {
    if (!term) return { visibleIds: null as Set<number> | null, matchIds: new Set<number>() };
    const visible = new Set<number>();
    const matches = new Set<number>();
    familles.forEach((f) => {
      if (!normalize(f.nom ?? "").includes(term)) return;
      matches.add(f.id);
      let cur: FamilleData | undefined = f;
      while (cur && !visible.has(cur.id)) {
        visible.add(cur.id);
        cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined;
      }
    });
    return { visibleIds: visible, matchIds: matches };
  }, [term, familles, byId]);

  // Sélection courante : déplier le chemin jusqu'à elle
  useEffect(() => {
    if (selectedId == null || !byId.has(selectedId)) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur = byId.get(selectedId);
      while (cur?.parent_id != null && !next.has(cur.parent_id)) {
        next.add(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
      return next;
    });
  }, [selectedId, byId]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(Array.from(childrenOf.keys())));
  const collapseAll = () => setExpanded(new Set());

  const renderNode = (node: FamilleData, depth: number, path: Set<number>): React.ReactNode => {
    if (path.has(node.id)) return null; // garde-fou cycle
    if (visibleIds && !visibleIds.has(node.id)) return null;
    const kids = childrenOf.get(node.id) ?? [];
    const hasKids = kids.length > 0;
    const isOpen = visibleIds ? true : expanded.has(node.id);
    const isExcluded = excluded.has(node.id);
    const isSelected = selectedId === node.id;
    const isMatch = matchIds.has(node.id);
    const nextPath = new Set(path).add(node.id);

    return (
      <React.Fragment key={node.id}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            pl: depth * 2.5,
            pr: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: isSelected ? "#eef4ff" : "transparent",
            "&:hover": { bgcolor: isExcluded ? "transparent" : isSelected ? "#eef4ff" : "action.hover" },
          }}
        >
          {hasKids ? (
            <IconButton size="small" onClick={() => toggle(node.id)} sx={{ p: 0.2, mr: 0.5 }} disabled={!!visibleIds}>
              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, flexShrink: 0 }} />
          )}
          <Typography
            variant="body2"
            onClick={() => !isExcluded && onSelect(node)}
            sx={{
              flex: 1,
              cursor: isExcluded ? "not-allowed" : "pointer",
              color: isExcluded ? "text.disabled" : isSelected ? "#2563eb" : "#101828",
              fontWeight: isSelected || isMatch ? 700 : depth === 0 ? 600 : 400,
              fontStyle: node.visible === false ? "italic" : "normal",
              userSelect: "none",
            }}
          >
            {node.nom}
            {hasKids && (
              <Typography component="span" variant="caption" sx={{ color: "#667085", ml: 0.75 }}>
                ({kids.length})
              </Typography>
            )}
          </Typography>
        </Box>
        {hasKids && isOpen && kids.map((c) => renderNode(c, depth + 1, nextPath))}
      </React.Fragment>
    );
  };

  const rendered = roots.map((r) => renderNode(r, 0, new Set()));
  const nothingVisible = visibleIds ? visibleIds.size === 0 : roots.length === 0;

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Rechercher dans l'arborescence…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={expandAll} disabled={!!visibleIds}>
          Tout déplier
        </Button>
        <Button size="small" startIcon={<UnfoldLessIcon />} onClick={collapseAll} disabled={!!visibleIds}>
          Tout replier
        </Button>
      </Box>

      <Box sx={{ maxHeight, overflowY: "auto", border: "1px solid #e4e7ec", borderRadius: 2, p: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : nothingVisible ? (
          <Typography variant="body2" sx={{ color: "#667085", p: 1 }}>
            {term ? "Aucune correspondance." : "Aucune donnée."}
          </Typography>
        ) : (
          rendered
        )}
      </Box>
    </Box>
  );
};

export default FamilleTreeView;
